import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  ArrowLeft, Check, AlertCircle, Instagram, Linkedin, 
  Twitter, Youtube, Music, HelpCircle, Shield
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platformIcons = {
  instagram: Instagram,
  linkedin: Linkedin,
  twitter: Twitter,
  youtube: Youtube,
  music: Music, // TikTok
};

const RegisterFollowPlaquePage = () => {
  const { user, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [codeValid, setCodeValid] = useState(null);
  const [codeError, setCodeError] = useState('');
  const [platformInfo, setPlatformInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    code: '',
    fullCode: '',
    redirectUrl: '',
    businessName: user?.business_name || ''
  });

  const verifyCode = async (code) => {
    if (code.length < 3) {
      setCodeValid(null);
      setCodeError('');
      setPlatformInfo(null);
      return;
    }
    
    setVerifying(true);
    try {
      const res = await axios.get(`${API}/plaques/verify/${code}`);
      const info = res.data;
      if (info.is_registered) {
        setCodeValid(false);
        setCodeError('This code is already registered');
        setPlatformInfo(null);
      } else if (info.type !== 'follow') {
        setCodeValid(false);
        setCodeError('This is a review plaque. Please use the review registration flow.');
        setPlatformInfo(null);
      } else {
        setCodeValid(true);
        setCodeError('');
        setFormData(prev => ({ ...prev, fullCode: info.code }));
        setPlatformInfo({ name: info.platform_name, prefix: info.prefix });
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setCodeValid(false);
        setCodeError('Invalid plaque code');
      } else {
        setCodeValid(false);
        setCodeError(error.response?.data?.detail || 'Error verifying code');
      }
      setPlatformInfo(null);
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (formData.code) {
        verifyCode(formData.code);
      }
    }, 500);
    
    return () => clearTimeout(debounce);
  }, [formData.code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step === 1) {
      if (!codeValid) {
        toast.error('Please enter a valid follow plaque code');
        return;
      }
      setStep(2);
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/plaques/register-follow`, {
        code: formData.fullCode || formData.code.toUpperCase(),
        redirect_url: formData.redirectUrl,
        business_name: formData.businessName
      });
      
      toast.success('Follow plaque registered successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register plaque');
    } finally {
      setLoading(false);
    }
  };

  const PlatformIcon = platformInfo ? (platformIcons[platformInfo.name.toLowerCase().split(' ')[0]] || Instagram) : Instagram;

  return (
    <div className="min-h-screen bg-[var(--color-bg-subtle)] py-8 px-6">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-[var(--color-text-secondary)] hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to dashboard
          </Link>
          {user?.is_admin && !isImpersonating && (
            <Link 
              to="/admin"
              className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 text-sm font-medium transition-colors"
              data-testid="admin-dashboard-link"
            >
              <Shield className="w-4 h-4" />
              Admin Dashboard
            </Link>
          )}
        </div>
        
        {/* Progress Steps */}
        <div className="flex items-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= 1 ? 'bg-primary text-white' : 'bg-gray-200'
            }`}>
              {step > 1 ? <Check className="w-4 h-4" /> : '1'}
            </div>
            <span className="text-sm font-medium hidden sm:inline">Enter Code</span>
          </div>
          <div className="flex-1 h-px bg-gray-200" />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= 2 ? 'bg-primary text-white' : 'bg-gray-200'
            }`}>
              2
            </div>
            <span className="text-sm font-medium hidden sm:inline">Configure</span>
          </div>
        </div>

        <Card className="shadow-layered border-0">
          <CardHeader>
            <CardTitle className="font-dashboard-heading text-xl font-bold">
              {step === 1 ? 'Enter Your Follow Plaque Code' : 'Configure Your Follow Plaque'}
            </CardTitle>
            <CardDescription>
              {step === 1 
                ? 'Enter the unique code from your follow plaque (starts with I, L, X, Y, or K)'
                : 'Set up where customers will be redirected when they scan'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {step === 1 ? (
                <div className="space-y-2">
                  <Label htmlFor="code">Follow Plaque Code</Label>
                  <div className="relative">
                    <Input
                      id="code"
                      type="text"
                      placeholder="e.g., 87XVH89VV"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      required
                      data-testid="follow-code-input"
                      className="h-12 text-lg font-mono uppercase pr-12"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {verifying && (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                      )}
                      {!verifying && codeValid === true && (
                        <Check className="w-5 h-5 text-green-500" />
                      )}
                      {!verifying && codeValid === false && (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      )}
                    </div>
                  </div>
                  {codeError && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {codeError}
                    </p>
                  )}
                  {codeValid && platformInfo && (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <Check className="w-4 h-4" />
                      <PlatformIcon className="w-4 h-4" />
                      Valid {platformInfo.name} follow plaque! Click continue to set it up.
                    </div>
                  )}
                  
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-[var(--color-text-secondary)]">
                      <strong>Follow plaque prefixes:</strong>
                    </p>
                    <ul className="text-sm text-[var(--color-text-muted)] mt-2 space-y-1">
                      <li>I = Instagram</li>
                      <li>L = LinkedIn</li>
                      <li>X = X (Twitter)</li>
                      <li>Y = YouTube</li>
                      <li>K = TikTok</li>
                    </ul>
                  </div>
                </div>
              ) : (
                <>
                  {platformInfo && (
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg mb-4">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                        <PlatformIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium">{platformInfo.name}</p>
                        <p className="text-sm text-[var(--color-text-muted)]">Follow Plaque</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="businessName">Business Name</Label>
                    <Input
                      id="businessName"
                      type="text"
                      placeholder="Your Business Name"
                      value={formData.businessName}
                      onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                      required
                      data-testid="follow-business-input"
                      className="h-11"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="redirectUrl">{platformInfo?.name || 'Social'} Profile URL</Label>
                    <Input
                      id="redirectUrl"
                      type="url"
                      placeholder={`https://${platformInfo?.name.toLowerCase().replace(/\s/g, '')}.com/yourbusiness`}
                      value={formData.redirectUrl}
                      onChange={(e) => setFormData({ ...formData, redirectUrl: e.target.value })}
                      required
                      data-testid="follow-url-input"
                      className="h-11"
                    />
                    <p className="text-xs text-[var(--color-text-muted)]">
                      Customers will be redirected to this URL when they scan your plaque
                    </p>
                    <a 
                      href="https://www.reviewmybusiness.ie/help/reivewurl" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-[var(--color-accent)] hover:underline mt-1"
                      data-testid="follow-url-help-link"
                    >
                      <HelpCircle className="w-4 h-4" />
                      Don't know what to put here? Click here for help.
                    </a>
                  </div>
                </>
              )}
              
              <div className="flex gap-3">
                {step === 2 && (
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 h-11"
                  >
                    Back
                  </Button>
                )}
                <Button 
                  type="submit" 
                  className="flex-1 h-11 rounded-full btn-lift"
                  disabled={loading || (step === 1 && !codeValid)}
                  data-testid="follow-submit-btn"
                >
                  {loading ? 'Registering...' : step === 1 ? 'Continue' : 'Register Follow Plaque'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterFollowPlaquePage;
